// Image processing utility functions
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const TARGET_SIZE = 320; // 320x320 pixels
const ALLOWED_FORMATS = [
  "image/jpeg",
  "image/jpg",
  "image/png",
  "image/gif",
  "image/webp",
];

export const validateFile = (file: File): string | null => {
  if (!ALLOWED_FORMATS.includes(file.type)) {
    return "Invalid file format. Please upload JPG, PNG, GIF, or WebP images only.";
  }
  if (file.size > MAX_FILE_SIZE) {
    return "File size too large. Maximum file size is 5MB.";
  }
  return null;
};

export const resizeAndCropImage = (file: File): Promise<File> => {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    const img = new Image();

    img.onload = () => {
      canvas.width = TARGET_SIZE;
      canvas.height = TARGET_SIZE;

      // Calculate crop dimensions (center crop)
      const minDimension = Math.min(img.width, img.height);
      const sx = (img.width - minDimension) / 2;
      const sy = (img.height - minDimension) / 2;

      ctx?.drawImage(
        img,
        sx,
        sy,
        minDimension,
        minDimension,
        0,
        0,
        TARGET_SIZE,
        TARGET_SIZE,
      );

      canvas.toBlob(
        (blob) => {
          if (blob) {
            // Create a new File object from the blob
            const processedFile = new File([blob], file.name, {
              type: "image/jpeg",
              lastModified: Date.now(),
            });
            resolve(processedFile);
          } else {
            reject(new Error("Failed to process image"));
          }
        },
        "image/jpeg",
        0.9,
      );
    };

    img.onerror = () => reject(new Error("Failed to load image"));
    img.src = URL.createObjectURL(file);
  });
};

/**
 * Get a data URL from a File for preview purposes
 */
export const getImagePreviewUrl = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(new Error("Failed to read file"));
    reader.readAsDataURL(file);
  });
};
